import React, { useId } from "react";

/*
 * Vintage poster artwork drawn as inline SVG: sunburst rays, swirled rays or
 * flowing waves on a cream base, finished with a worn-print speckle texture.
 */

export interface Art {
  kind: "burst" | "swirl" | "waves";
  base: string;
  colors: string[];
  /** swirl strength in radians (swirl only) */
  twist?: number;
  /** optional sun disc at the centre */
  sun?: string;
}

const W = 400;
const H = 250;
const CX = 200;
const CY = 170;
const R = 520;

const pt = (a: number, r: number, cy: number) => `${(CX + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;

function rays(colors: string[], twist: number, cy: number) {
  const wedges = colors.length * Math.max(1, Math.round(15 / colors.length));
  const step = Math.PI / wedges; // every other slot is a coloured wedge
  // each wedge edge bends further the farther it is from the centre
  const edge = (a: number) => Array.from({ length: 16 }, (_, k) => pt(a + twist * (k / 15) ** 1.15, (k / 15) * R, cy));
  return Array.from({ length: wedges }, (_, i) => {
    const a0 = i * 2 * step;
    const d = `M${[...edge(a0), ...edge(a0 + step).reverse()].join("L")}Z`;
    return <path key={i} d={d} fill={colors[i % colors.length]} />;
  });
}

function waves(colors: string[]) {
  const band = 46;
  const xs = Array.from({ length: 31 }, (_, k) => -150 + k * 25);
  const line = (y: number, i: number) => xs.map((x) => `${x},${(y + 26 * Math.sin(x / 75 + i * 0.7)).toFixed(1)}`);
  return Array.from({ length: 10 }, (_, j) => {
    const i = j - 3;
    const top = line(i * band, i);
    const bottom = line(i * band + band * 0.62, i).reverse();
    return <path key={j} d={`M${[...top, ...bottom].join("L")}Z`} fill={colors[j % colors.length]} />;
  });
}

export const RetroArt: React.FC<{ art: Art }> = ({ art }) => {
  const id = useId().replace(/:/g, "");
  // a sun sits just above the screenshot, so the rays radiate from there
  const cy = art.sun ? 84 : CY;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" className="w-full h-full" aria-hidden="true">
      <defs>
        {/* worn print: fine cream specks, clustered in coarse patches */}
        <filter id={`${id}w`} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.95" numOctaves="1" seed="4" result="fine" />
          <feTurbulence type="fractalNoise" baseFrequency="0.022 0.035" numOctaves="2" seed="9" result="coarse" />
          <feColorMatrix in="coarse" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  5 0 0 0 -2.2" result="mask" />
          <feColorMatrix in="fine" values="0 0 0 0 .96  0 0 0 0 .93  0 0 0 0 .85  8 0 0 0 -4.4" result="specks" />
          <feComposite in="specks" in2="mask" operator="in" />
        </filter>
        <filter id={`${id}g`} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="1.4" numOctaves="1" seed="2" />
          <feColorMatrix values="0 0 0 0 .96  0 0 0 0 .93  0 0 0 0 .85  6 0 0 0 -3.9" />
        </filter>
      </defs>
      <rect width={W} height={H} fill={art.base} />
      {art.kind === "waves" ? (
        <g transform={`rotate(-16 ${W / 2} ${H / 2})`}>{waves(art.colors)}</g>
      ) : (
        rays(art.colors, art.kind === "swirl" ? (art.twist ?? 1) : 0, cy)
      )}
      {art.sun && <circle cx={CX} cy={cy} r="44" fill={art.sun} />}
      <rect width={W} height={H} filter={`url(#${id}w)`} />
      <rect width={W} height={H} filter={`url(#${id}g)`} opacity="0.6" />
    </svg>
  );
};
